import {
	parsePartNoList,
	readParametricByCode,
	sortPoolByPartNos,
	type ParametricPartNoSource
} from "./parametricPartNos";

// Re-exported so the multi-pack module stays the single import for its callers.
export { parsePartNoList, sortPoolByPartNos };

/*
	Multi pack. A plain Norce product that is never bought as itself:
	its PDP lets the customer compose `packSize` units out of a fixed item
	pool, and the basket receives those items as independent lines — the mix
	pack product never enters the cart, so its own stock and quantity are
	presentational only.

	Three catalogue parametrics drive it, mapped per deployment because the
	codes are owned by the catalogue, not by us:
	  partNos → text field listing the item PartNos ("204960, 204959, …")
	  total   → units in one pack (24 for a 24-pack); the same field the
	            container deposit reads for its per-unit count
	  amounts → multiple-list of the amounts a single item may be picked in
	            (4, 6, 8, 12). Unset means any amount, stepping by one.
	An empty config map turns the whole feature off.
*/

export interface MultiPackConfig {
	/** Parametric code of the text field holding the item PartNos. */
	partNos: string;
	/** Parametric code holding the pack's unit count. */
	total: string;
	/** Parametric code holding the allowed per-item amounts. */
	amounts: string;
	/**
	 * Step to fall back on when the `amounts` parametric is unset — allowed
	 * amounts become its multiples up to the pack size. `"0"` opts out and
	 * restores free choice. Unset = `DEFAULT_MULTIPACK_STEP`.
	 */
	defaultStep?: string;
}

export interface MultiPackMeta {
	/** Item PartNos in catalogue order. */
	partNos: string[];
	/** Units one pack holds (24 for a 24-pack). */
	packSize: number;
	/** Allowed per-item amounts, ascending. Empty = free choice. */
	amounts: number[];
}

export const isMultiPackEnabled = (
	config: Partial<MultiPackConfig> | undefined | null
): config is MultiPackConfig => !!config?.partNos;

/** "12, 4, 8, 4, x, 0" → [4, 8, 12]: positive integers, deduped, ascending. */
export const parseAmountList = (value: string | undefined): number[] =>
	[
		...new Set(
			String(value ?? "")
				.split(/[,;\s]+/)
				.map((amount) => Number.parseInt(amount.trim(), 10))
				.filter((amount) => Number.isFinite(amount) && amount > 0)
		)
	].sort((a, b) => a - b);

/** Fallback step when the catalogue leaves `amounts` unset. */
export const DEFAULT_MULTIPACK_STEP = 4;

/** Multiples of `step` up to `packSize`: (4, 24) → [4, 8, 12, 16, 20, 24]. */
export const buildStepAmounts = (packSize: number, step: number): number[] => {
	if (!Number.isFinite(step) || step <= 0) return [];
	const amounts: number[] = [];
	for (let amount = step; amount <= packSize; amount += step) {
		amounts.push(amount);
	}
	return amounts;
};

/**
 * A product's multi-pack meta (variant first, then the parent — each field
 * falls back on its own). Null for a non-pack, which is a product carrying no
 * item list, and for a pack with no pack size, which would let any selection
 * check out. `partNo` is a second guard: a product among its own choices is
 * never the pack.
 */
export const getMultiPackMeta = (
	sources: (ParametricPartNoSource | undefined | null)[],
	config: MultiPackConfig,
	partNo?: string | null
): MultiPackMeta | null => {
	const partNos = parsePartNoList(
		readParametricByCode(sources, config.partNos)
	);
	if (!partNos.length) return null;
	if (partNo && partNos.includes(partNo)) return null;

	const packSize = Number.parseInt(
		String(readParametricByCode(sources, config.total) ?? ""),
		10
	);
	if (!Number.isFinite(packSize) || packSize <= 0) return null;

	// An unset `amounts` parametric would step by one — 24 clicks to fill a
	// 24-pack. Fall back to multiples of the configured step instead.
	const amounts = parseAmountList(
		readParametricByCode(sources, config.amounts)
	);
	const step = config.defaultStep
		? Number.parseInt(config.defaultStep, 10)
		: DEFAULT_MULTIPACK_STEP;

	return {
		partNos,
		packSize,
		amounts: amounts.length ? amounts : buildStepAmounts(packSize, step)
	};
};

/**
 * Next amount the customer can step an item up to, or `current` when the
 * step does not fit in `max`. `amounts` empty = plain +1.
 */
export const nextAmount = (
	current: number,
	amounts: number[],
	max: number
): number => {
	if (!amounts.length) return current < max ? current + 1 : current;
	return amounts.find((amount) => amount > current && amount <= max) ?? current;
};

/**
 * Pull a typed amount into what the pack actually allows: clamped to
 * `[0, max]`, and — when `amounts` is configured — down to the nearest allowed
 * value. A number below the smallest allowed amount becomes that smallest one
 * if it fits, otherwise zero, so typing "1" into a 2/4/6/8 pack lands on 2
 * rather than silently clearing the row.
 */
export const snapAmount = (
	value: number,
	amounts: number[],
	max: number
): number => {
	const ceiling = Math.max(0, Math.floor(max));
	const wanted = Math.min(
		Math.max(0, Number.isFinite(value) ? Math.floor(value) : 0),
		ceiling
	);
	if (!amounts.length || wanted === 0) return wanted;

	const fits = amounts.filter((amount) => amount <= ceiling);
	if (!fits.length) return 0;
	const downwards = fits.filter((amount) => amount <= wanted);
	return downwards.length ? Math.max(...downwards) : Math.min(...fits);
};

/** Previous amount, floored at zero. `amounts` empty = plain −1. */
export const previousAmount = (current: number, amounts: number[]): number => {
	if (current <= 0) return 0;
	if (!amounts.length) return current - 1;
	return amounts.reduce(
		(best, amount) => (amount < current ? Math.max(best, amount) : best),
		0
	);
};

/**
 * Buyable stock of one item; 0 when it cannot be added at all. Warehouse only,
 * deliberately narrower than `getPackItemStock`, which adds `OnHandStore`: a
 * multi pack ships as one consignment, so shop stock cannot build it. Confirm
 * with the client before making the two agree.
 */
export const getWarehouseStock = (packItem: {
	IsBuyable?: boolean;
	OnHand?: { Value?: number } | null;
}): number =>
	packItem.IsBuyable === false ? 0 : Math.max(0, packItem.OnHand?.Value ?? 0);

/** Selections map → flat basket quantities, dropping empty picks. */
export const toMultiPackLines = (
	selections: Record<string, number>,
	partNos: string[]
): { PartNo: string; Quantity: number }[] =>
	partNos
		.map((PartNo) => ({ PartNo, Quantity: selections[PartNo] ?? 0 }))
		.filter((line) => line.Quantity > 0);

/** Total units currently selected. */
export const getSelectedTotal = (selections: Record<string, number>): number =>
	Object.values(selections).reduce((sum, amount) => sum + amount, 0);

/**
 * Bring a selection back inside `target` after the pack count was lowered,
 * dropping units from the end of the pool first (the customer's most recent
 * picks) and snapping whatever is kept onto an allowed amount — a leftover on
 * a disallowed amount would still satisfy the total and check out.
 */
export const trimSelections = (
	selections: Record<string, number>,
	partNos: string[],
	target: number,
	amounts: number[]
): Record<string, number> => {
	if (getSelectedTotal(selections) <= target) return selections;

	const trimmed = { ...selections };
	for (const partNo of [...partNos].reverse()) {
		const total = getSelectedTotal(trimmed);
		if (total <= target) break;
		const current = trimmed[partNo] ?? 0;
		if (!current) continue;
		const room = Math.max(0, current - (total - target));
		const kept = snapAmount(room, amounts, room);
		if (kept) trimmed[partNo] = kept;
		else delete trimmed[partNo];
	}
	return trimmed;
};

/**
 * Availability of the pack as a whole. The multi-pack product carries no stock
 * of its own — it never enters the basket — so the pool's combined stock is
 * what add-to-cart may judge it by.
 */
export const getPoolStock = (
	pool: { IsBuyable?: boolean; OnHand?: { Value?: number } | null }[]
): number =>
	pool.reduce((sum, packItem) => sum + getWarehouseStock(packItem), 0);

/** What the customer picked, as the summary lists it. */
export const toPackPicks = (
	lines: { item: { PartNo: string; Quantity: number; Name?: string } }[]
): { partNo: string; name: string; quantity: number }[] =>
	lines.map(({ item }) => ({
		partNo: item.PartNo,
		name: item.Name ?? item.PartNo,
		quantity: item.Quantity
	}));

/**
 * The picks as catalogue products plus amounts, for the analytics report —
 * basket lines alone lack the Id and price GTM needs. Lines whose product is
 * no longer in the pool are dropped rather than reported half-empty.
 */
export const toPickedProducts = <T extends { PartNo: string }>(
	lines: { item: { PartNo: string; Quantity: number } }[],
	pool: T[]
): { product: T; quantity: number }[] =>
	lines.flatMap(({ item }) => {
		const product = pool.find((candidate) => candidate.PartNo === item.PartNo);
		return product ? [{ product, quantity: item.Quantity }] : [];
	});
